import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import useOverviewContext, { OverviewAccordionRoot } from '../../context/OverviewContextProvider'
import { useIsMobile, useMobileDetailRouteFallback, useShowDetailsPane } from '../../hooks'
import { Colors, Dimensions, Spacing } from '../../styles'
import { icons } from '../../styles/images'
import { Motion } from '../../styles/motion'
import { getActiveMeetingPreparationTasks, getFirstTaskInOverviewView, isTaskOverviewView } from '../../utils/overview'
import { TOverviewView, TTaskV4 } from '../../utils/types'
import Flex from '../atoms/Flex'
import Spinner from '../atoms/Spinner'
import GTButton from '../atoms/buttons/GTButton'
import { BodySmall } from '../atoms/typography/Typography'
import CalendarPlanOffer from '../calendar/CalendarPlanOffer'
import GoalReviewNudge from '../goals/GoalReviewNudge'
import NoGoalNudge from '../goals/NoGoalNudge'
import { useOptionalGoalCreation } from '../goals/creation/shared/GoalCreationContext'
import { Header } from '../molecules/Header'
import AccordionItem from '../overview/AccordionItem'
import EditModal from '../overview/EditModal'
import EmptyOverview from '../overview/EmptyOverview'
import OverviewDetails, { useOverviewDetailState } from '../overview/OverviewDetails'
import useOverviewCardOrder from '../overview/useOverviewCardOrder'
import GTDropdownMenu from '../radix/GTDropdownMenu'
import ScrollableListTemplate from '../templates/ScrollableListTemplate'

const MoveStatus = styled(BodySmall)`
    display: block;
    min-height: ${Spacing._16};
    margin: ${Spacing._4} ${Spacing._4} 0;
    color: ${Colors.text.muted};
`
const listPaneIn = keyframes`
    from { opacity: 0; transform: translateX(-${Motion.overlayOffset}); }
    to { opacity: 1; transform: translateX(0); }
`
const OverviewListPane = styled(Flex)`
    ${Dimensions.mediaQuery.phone} {
        animation: ${listPaneIn} ${Motion.enter};
    }
`

const useSelectSeededOverviewItem = (
    isMobile: boolean,
    isLoading: boolean,
    lists: TOverviewView[],
    activeMeetingPreparationTasks: TTaskV4[],
    overviewItemId?: string
) => {
    const { openListIds } = useOverviewContext()
    const hasSelected = useRef(false)
    const navigate = useNavigate()

    useLayoutEffect(() => {
        if (hasSelected.current || isLoading) return
        hasSelected.current = true

        if (isMobile || overviewItemId) return
        const firstSeededView = lists.find((view) => openListIds.includes(view.id))
        if (!firstSeededView || !isTaskOverviewView(firstSeededView)) {
            navigate('/overview', { replace: true })
            return
        }
        const firstItem = getFirstTaskInOverviewView(firstSeededView, activeMeetingPreparationTasks)
        navigate(firstItem ? `/overview/${firstSeededView.id}/${firstItem.id}` : '/overview', { replace: true })
    }, [activeMeetingPreparationTasks, isLoading, isMobile, lists, navigate, openListIds, overviewItemId])
}

const DailyOverviewView = () => {
    const [isEditListsModalOpen, setIsEditListsModalOpen] = useState(false)
    const [isListActionsMenuOpen, setIsListActionsMenuOpen] = useState(false)
    const isMobile = useIsMobile()
    const { overviewViewId, overviewItemId, subtaskId } = useParams()
    // Absent when the goals feature is off for this user.
    const goalCreation = useOptionalGoalCreation()

    const showDetailsPane = useShowDetailsPane()
    const { expandAll, collapseAll, announcement } = useOverviewContext()

    const {
        lists,
        meetingPreparationTasks,
        isLoading: isOverviewDetailLoading,
        hasSelectedOverviewDetail,
    } = useOverviewDetailState()
    const activeMeetingPreparationTasks = useMemo(
        () => getActiveMeetingPreparationTasks(meetingPreparationTasks),
        [meetingPreparationTasks]
    )
    useSelectSeededOverviewItem(isMobile, isOverviewDetailLoading, lists, activeMeetingPreparationTasks, overviewItemId)
    const cardOrder = useOverviewCardOrder(lists)
    const selectedOverviewItemId = subtaskId || overviewItemId
    const shouldShowMobileDetailSpinner = useMobileDetailRouteFallback({
        isMobile,
        detailId: overviewViewId && overviewItemId ? selectedOverviewItemId : undefined,
        canValidate: !isOverviewDetailLoading,
        hasSelectedDetail: hasSelectedOverviewDetail,
        listPath: '/overview',
    })

    if (isOverviewDetailLoading) return <Spinner />
    return (
        <>
            <OverviewListPane>
                <ScrollableListTemplate>
                    <Header
                        folderName="Daily Overview"
                        rightActions={
                            <>
                                {!isMobile && (
                                    <GTButton
                                        styleType="icon"
                                        icon={icons.gear}
                                        tooltipText="Customize Daily Overview"
                                        onClick={() => setIsEditListsModalOpen(true)}
                                    />
                                )}
                                <GTDropdownMenu
                                    isOpen={isListActionsMenuOpen}
                                    setIsOpen={setIsListActionsMenuOpen}
                                    hideCheckmark
                                    align="end"
                                    items={[
                                        {
                                            label: 'Collapse all',
                                            icon: icons.squareMinus,
                                            onClick: () => {
                                                collapseAll()
                                                setIsListActionsMenuOpen(false)
                                            },
                                        },
                                        {
                                            label: 'Expand all',
                                            icon: icons.squarePlus,
                                            onClick: () => {
                                                expandAll()
                                                setIsListActionsMenuOpen(false)
                                            },
                                        },
                                        {
                                            label: 'Customize Daily Overview',
                                            icon: icons.gear,
                                            onClick: () => {
                                                setIsListActionsMenuOpen(false)
                                                setIsEditListsModalOpen(true)
                                            },
                                        },
                                    ]}
                                    trigger={
                                        <GTButton
                                            styleType="icon"
                                            icon={icons.ellipsisVertical}
                                            tooltipText="List actions"
                                        />
                                    }
                                />
                            </>
                        }
                    />
                    {goalCreation && (
                        <>
                            <NoGoalNudge />
                            <GoalReviewNudge />
                        </>
                    )}
                    <CalendarPlanOffer />
                    {/* Notices above stay fixed; only the cards below follow the saved order. */}
                    {lists.length === 0 && <EmptyOverview onAddLists={() => setIsEditListsModalOpen(true)} />}
                    <OverviewAccordionRoot>
                        {lists.map((list, index) => (
                            <AccordionItem
                                key={list.id}
                                list={list}
                                lists={lists}
                                activeMeetingPreparationTasks={activeMeetingPreparationTasks}
                                showCollapsedPreview
                                order={{ index, actions: cardOrder.getActions(index), onDrop: cardOrder.onDrop }}
                            />
                        ))}
                    </OverviewAccordionRoot>
                    {/* A bare live region, not role="status": other views already own the page's status role. */}
                    <MoveStatus aria-live="polite" data-testid="overview-move-status">
                        {cardOrder.announcement || announcement}
                    </MoveStatus>
                </ScrollableListTemplate>
            </OverviewListPane>
            {showDetailsPane &&
                (!isMobile || overviewItemId) &&
                (shouldShowMobileDetailSpinner ? <Spinner /> : <OverviewDetails />)}
            <EditModal isOpen={isEditListsModalOpen} setisOpen={setIsEditListsModalOpen} />
        </>
    )
}

export default DailyOverviewView
